import { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  endDate: Date | undefined;
}

const useCountdown = ({ endDate }: Props) => {
  const [countdownDays, setTimerDays] = useState<string | number>("00");
  const [countdownHours, setTimerHours] = useState<string | number>("00");
  const [countdownMinutes, setTimerMinutes] = useState<string | number>("00");
  const [countdownSeconds, setTimerSeconds] = useState<string | number>("00");
  const [countdownComplete, setCountdownComplete] = useState(false);

  const interval = useRef<number>();
  const dayInMilliseconds = 86400000;

  const formatTime = (property: string) => {
    if (Number(property) < 10) {
      return `0${property}`;
    }
    return property;
  };

  const updateCountdown = useCallback(() => {
    if (endDate === undefined) {
      return;
    }

    interval.current = window.setInterval(() => {
      const now = Date.now();
      const distance = endDate.getTime() - now;

      if (distance <= 0) {
        clearInterval(interval.current);
        setCountdownComplete(true);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24)).toString();
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      ).toString();
      const minutes = Math.floor(
        (distance % (1000 * 60 * 60)) / (1000 * 60),
      ).toString();
      const seconds = Math.floor((distance % (1000 * 60)) / 1000).toString();

      const formattedDays = formatTime(days);
      const formattedHours = formatTime(hours);
      const formattedMinutes = formatTime(minutes);
      const formattedSeconds = formatTime(seconds);

      if (distance > dayInMilliseconds) {
        setTimerDays(formattedDays);
      }
      setTimerHours(formattedHours);
      setTimerMinutes(formattedMinutes);
      setTimerSeconds(formattedSeconds);
    }, 1000);
  }, [endDate]);

  useEffect(() => {
    setCountdownComplete(false);
  }, [endDate]);

  useEffect(() => {
    if (!countdownComplete) {
      updateCountdown();
    }

    return () => clearInterval(interval.current);
  }, [countdownComplete, updateCountdown]);

  return {
    countdownDays,
    countdownHours,
    countdownMinutes,
    countdownSeconds,
    countdownDisplay: `${
      countdownDays === "00" ? "" : `${countdownDays}:`
    }${countdownHours}:${countdownMinutes}:${countdownSeconds}`,
    countdownComplete,
  };
};

export default useCountdown;
